// Cista logika ekrana "Pregled iznimki" izvoda (MainLedgerIzvodPregled.vue):
// razvrstavanje stavki, upozorenja kod odabira konta, brzi izbor konta i provjere
// prije zatvaranja izvoda. Bez Vuea i bez poziva na server, da se moze probati
// samostalno: server/helpers/izvodPregled.test.js.
//
// Server je mjerodavan za iznos, smjer i partnera (potvrda ih cita sa stavke u bazi);
// ovdje se samo odlucuje sto korisniku pokazati i na sto ga upozoriti.

const n2 = (v) => Math.round((parseFloat(v) || 0) * 100) / 100

const PRAG_VELIKI_IZNOS = 5000

const tekst = (opis) => String(opis == null ? '' : opis)
  .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

const RE_POVRAT = /storno|povrat|preplat|refund|reklamacij|ispravak/

const jePovrat = (opis) => RE_POVRAT.test(tekst(opis))

const prefiks = (konto, n) => String(konto || '').trim().substring(0, n)

const imaPartnera = (partner) => !!(partner && (String(partner.oib || '').trim() || String(partner.naziv || '').trim()))

// Stavke koje se ne mogu knjiziti na ovom ekranu: strana valuta, bez jasnog smjera.
// Idu u stari ekran (razdioba na vise konta, tecaj).
const trebaStariEkran = (s) => {
  if (!s.smjer) { return true }
  return (s.razlozi || []).some(r => /valuti/i.test(r))
}

/**
 * Upozorenja za izbor konta jedne stavke.
 * @param {Object} o {konto, smjer: 'ulaz'|'izlaz', iznos, opis, partner, kontoBanke, plan}
 *        plan: objekt {konto: true} ili lista {account4} - sluzi samo za provjeru da konto postoji.
 * @returns {Array} [{razina: 'greska'|'upozorenje', tekst}] - 'greska' blokira potvrdu.
 */
const upozorenjaKonta = (o) => {
  const rez = []
  const konto = String(o.konto || '').trim()
  if (!konto) {
    rez.push({razina: 'greska', tekst: 'Odaberite konto.'})
    return rez
  }
  if (o.plan && !o.plan[konto]) {
    rez.push({razina: 'greska', tekst: 'Konto ' + konto + ' ne postoji u kontnom planu.'})
    return rez
  }
  if (o.kontoBanke && konto === String(o.kontoBanke).trim()) {
    rez.push({razina: 'greska', tekst: 'Konto banke (' + konto + ') ne smije biti protukonto - redak banke dodaje se pri zatvaranju izvoda.'})
    return rez
  }
  const povrat = jePovrat(o.opis)
  const p2 = prefiks(konto, 2)
  const p1 = prefiks(konto, 1)
  if (o.smjer === 'ulaz') {
    if (p2 === '22' && !povrat) {
      rez.push({razina: 'upozorenje', tekst: 'Uplata na konto dobavljača (' + konto + ') bez oznake povrata u opisu. Je li to povrat dobavljača?'})
    }
    if ((p1 === '4' || p1 === '5') && !povrat) {
      rez.push({razina: 'upozorenje', tekst: 'Uplata na konto troškova (' + konto + '). Je li to povrat troška?'})
    }
  }
  if (o.smjer === 'izlaz') {
    if (p2 === '12' && !povrat) {
      rez.push({razina: 'upozorenje', tekst: 'Isplata na konto kupaca (' + konto + ') bez oznake povrata u opisu. Je li to povrat kupcu?'})
    }
    if ((p1 === '6' || p1 === '7') && !povrat) {
      rez.push({razina: 'upozorenje', tekst: 'Isplata na konto prihoda (' + konto + '). Je li to povrat prihoda?'})
    }
  }
  if (n2(o.iznos) >= PRAG_VELIKI_IZNOS) {
    rez.push({razina: 'upozorenje', tekst: 'Veći iznos (od 5.000,00): potvrdite konto pažljivo.'})
  }
  if (!imaPartnera(o.partner)) {
    rez.push({razina: 'upozorenje', tekst: 'Partner nije poznat: potvrdite konto pažljivo ili dodijelite partnera.'})
  }
  return rez
}

const blokira = (upozorenja) => (upozorenja || []).some(u => u.razina === 'greska')

/**
 * Brzi izbor konta ("zetoni") samo za smjer stavke.
 * @param {Object} o {smjer, opis, savjet, kandidati: [{konto, naziv, broj, postoji}], postoji: fn(konto)}
 * @returns {Array} [{konto, naziv, izvor: 'savjet'|'povijest'|'smjer'}] bez duplikata
 */
const brziKonta = (o) => {
  const postoji = o.postoji || (() => true)
  const rez = []
  const dodaj = (konto, naziv, izvor) => {
    konto = String(konto || '').trim()
    if (!konto || !postoji(konto) || rez.some(r => r.konto === konto)) { return }
    rez.push({konto, naziv, izvor})
  }
  if (o.savjet) { dodaj(o.savjet, 'Savjet', 'savjet') }
  ;(o.kandidati || []).forEach(k => { if (k.postoji !== false) { dodaj(k.konto, 'Prije knjiženo' + (k.broj ? ' (' + k.broj + 'x)' : ''), 'povijest') } })
  const povrat = jePovrat(o.opis)
  if (o.smjer === 'ulaz') {
    dodaj('1200', 'Kupac', 'smjer')
    if (povrat) { dodaj('2200', 'Povrat dobavljača', 'smjer') }
  } else if (o.smjer === 'izlaz') {
    dodaj('2200', 'Dobavljač', 'smjer')
    if (povrat) { dodaj('1200', 'Povrat kupcu', 'smjer') }
  }
  return rez
}

/**
 * Razvrstavanje stavki iz prijedloga.
 * Spremno: visoka/srednja razina, konto postoji, nema blokade i ne treba stari ekran.
 * Ostalo traži odluku.
 */
const razvrstaj = (stavke) => {
  const spremne = []
  const odluka = []
  ;(stavke || []).forEach(s => {
    const spremno = s.razina !== 'rucno' && !!s.konto && s.kontoPostoji !== false && !trebaStariEkran(s)
    ;(spremno ? spremne : odluka).push(s)
  })
  return {spremne, odluka}
}

/** Razlozi zasto stavka trazi odluku, kao popis kratkih recenica. */
const razloziOdluke = (s, kontoBanke) => {
  const rez = []
  if (s.razlog) { rez.push(s.razlog) }
  ;(s.razlozi || []).slice(1).forEach(r => { if (rez.indexOf(r) < 0) { rez.push(r) } })
  if (s.konto && s.kontoPostoji === false) { rez.push('Predloženi konto ' + s.konto + ' ne postoji u kontnom planu.') }
  if (n2(s.iznos) >= PRAG_VELIKI_IZNOS) { rez.push('Veći iznos: potvrdite konto pažljivo.') }
  if (!imaPartnera(s.partner)) { rez.push('Nema partnera na stavci.') }
  if (kontoBanke && s.konto && String(s.konto).trim() === String(kontoBanke).trim()) { rez.push('Predloženi konto je konto banke.') }
  return rez
}

/**
 * Provjere prije zatvaranja izvoda.
 * @param {Object} o {glava: {bankStatmentStart, bankStatmentDebit, bankStatmentCredit, bankStatmentFinish, ClosedAccount},
 *                    sides: sve stavke izvoda (MainLedgerSides), kontoBanke}
 * @returns {Object} {stavke: [{ok, tekst}], neproknjizeno, brojNeproknjizenih, sveOk, mozeZatvoriti, zatvoren}
 */
const provjere = (o) => {
  const glava = o.glava || {}
  const sides = o.sides || []
  const dug = n2(sides.reduce((a, s) => a + (parseFloat(s.bankStatmentDebit) || 0), 0))
  const pot = n2(sides.reduce((a, s) => a + (parseFloat(s.bankStatmentCredit) || 0), 0))
  const otvorene = sides.filter(s => s.ClosedAccount !== true)
  const neproknjizeno = n2(otvorene.reduce((a, s) => a + Math.abs(parseFloat(s.bankStatmentDebit) || 0) + Math.abs(parseFloat(s.bankStatmentCredit) || 0), 0))
  const gDug = n2(glava.bankStatmentDebit)
  const gPot = n2(glava.bankStatmentCredit)
  const ocekivanoZavrsno = n2(n2(glava.bankStatmentStart) - gDug + gPot)
  const stavke = []
  const zbrojOk = dug === gDug && pot === gPot
  stavke.push({
    ok: zbrojOk,
    tekst: zbrojOk
      ? 'Zbrojevi stavki jednaki su prometu izvoda (' + fmt(dug) + ' / ' + fmt(pot) + ')'
      : 'Zbrojevi stavki (' + fmt(dug) + ' / ' + fmt(pot) + ') razlikuju se od prometa izvoda (' + fmt(gDug) + ' / ' + fmt(gPot) + ')'
  })
  const zavrsnoOk = ocekivanoZavrsno === n2(glava.bankStatmentFinish)
  stavke.push({
    ok: zavrsnoOk,
    tekst: zavrsnoOk
      ? 'Završno stanje = početno − duguje + potražuje'
      : 'Završno stanje (' + fmt(glava.bankStatmentFinish) + ') nije jednako početno − duguje + potražuje (' + fmt(ocekivanoZavrsno) + ')'
  })
  const bankaOk = !!String(o.kontoBanke || '').trim()
  stavke.push({
    ok: bankaOk,
    tekst: bankaOk ? 'Račun banke ' + String(o.kontoBanke).trim() + ' postavljen' : 'Konto banke nije postavljen (Bankovni računi, polje konto)'
  })
  stavke.push({
    ok: otvorene.length === 0,
    tekst: otvorene.length === 0 ? 'Sve stavke proknjižene' : otvorene.length + (otvorene.length === 1 ? ' stavka još nije proknjižena' : ' stavki još nije proknjiženo')
  })
  stavke.push({
    ok: neproknjizeno === 0,
    tekst: neproknjizeno === 0 ? 'Neproknjiženi iznos je 0,00' : 'Neproknjiženi iznos mora biti 0,00 (sada ' + fmt(neproknjizeno) + ')'
  })
  const zatvoren = glava.ClosedAccount === true
  const sveOk = stavke.every(s => s.ok)
  // Zbroj i stanje mogu biti nepodudarni zbog uvoza; to je upozorenje, ali samo
  // neproknjiženo i konto banke stvarno sprjecavaju zatvaranje.
  const mozeZatvoriti = otvorene.length === 0 && neproknjizeno === 0 && bankaOk && !zatvoren
  return {stavke, neproknjizeno, brojNeproknjizenih: otvorene.length, sveOk, mozeZatvoriti, zatvoren}
}

function fmt (v) {
  const n = parseFloat(v)
  return (isNaN(n) ? 0 : n).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

export {
  PRAG_VELIKI_IZNOS, n2, fmt, jePovrat, imaPartnera, trebaStariEkran, upozorenjaKonta, blokira,
  brziKonta, razvrstaj, razloziOdluke, provjere
}
